import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NumberLine from './NumberLine';

// Canned examples used in the notation guide accordion. Each row shows a
// notation chip alongside a mini read-only number line rendered with the same
// NumberLine component the student answers on, so the legend stays in sync.
const NOTATION_EXAMPLES = [
  {
    notation: '3',
    description: 'A single point',
    elements: [{ type: 'point', value: 3 }],
  },
  {
    notation: '[-1, 3]',
    description: 'Closed interval — both endpoints included',
    elements: [{ type: 'interval', start: -1, end: 3, startOpen: false, endOpen: false }],
  },
  {
    notation: '[-3, 1)',
    description: 'Left endpoint included, right endpoint excluded',
    elements: [{ type: 'interval', start: -3, end: 1, startOpen: false, endOpen: true }],
  },
  {
    notation: '(-inf, 0]',
    description: 'Ray to negative infinity, 0 included',
    elements: [{ type: 'ray', origin: 0, direction: 'left', originOpen: false }],
  },
  {
    notation: '[2, +inf)',
    description: 'Ray to positive infinity, 2 included',
    elements: [{ type: 'ray', origin: 2, direction: 'right', originOpen: false }],
  },
];

const LEGEND_CONFIG = {
  min: -5,
  max: 5,
  step: 1,
  labelInterval: 1,
  showTicks: true,
  showLabels: true,
  snapToTicks: true,
  tolerance: 0.01,
  maxElements: 20,
};

const NotationGuide = () => (
  <Accordion
    disableGutters
    sx={{
      backgroundColor: 'rgba(33, 150, 243, 0.04)',
      border: '1px solid rgba(33, 150, 243, 0.2)',
      borderRadius: 1,
      '&:before': { display: 'none' },
    }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography variant="subtitle2" fontWeight="bold">
        Notation guide — how interval notation looks on the number line
      </Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ pb: 1, borderBottom: '1px dashed rgba(0,0,0,0.15)' }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Endpoint symbols:</strong>
          </Typography>
          <Typography variant="body2" component="div" sx={{ pl: 1 }}>
            <div>
              <strong>● Filled circle</strong> = <strong>Inclusive</strong> — the value <em>is</em> included
              (square bracket <code>[</code> or <code>]</code>, e.g. <code>x ≤ 3</code>)
            </div>
            <div>
              <strong>○ Open circle</strong> = <strong>Exclusive</strong> — the value is <em>not</em> included
              (parenthesis <code>(</code> or <code>)</code>, e.g. <code>x &lt; 3</code>)
            </div>
            <div>
              Infinity (<code>-inf</code>, <code>+inf</code>) is always written with a parenthesis since it cannot be reached.
            </div>
          </Typography>
        </Box>
        {NOTATION_EXAMPLES.map((ex) => (
          <Box
            key={ex.notation}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ minWidth: 110 }}>
              <Chip label={ex.notation} size="small" sx={{ fontFamily: 'monospace' }} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {ex.description}
              </Typography>
            </Box>
            <NumberLine
              config={LEGEND_CONFIG}
              elements={ex.elements}
              readOnly={true}
              width={340}
              height={70}
              padding={25}
            />
          </Box>
        ))}
      </Box>
    </AccordionDetails>
  </Accordion>
);

export default NotationGuide;
